import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { SearchableSelect } from '../components/ui/searchable-select';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  FileText, 
  Plus, 
  Trash2,
  ArrowLeft,
  Download,
  Calendar,
  Loader2,
  Building,
  CreditCard,
  Users,
  Languages
} from 'lucide-react';

const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 
                   'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_UA = ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
                   'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

const CURRENCIES = [
  { code: 'USD', symbol: '$', name_en: 'USD', name_ua: 'Долари США' },
  { code: 'EUR', symbol: '€', name_en: 'EUR', name_ua: 'Євро' },
  { code: 'GBP', symbol: '£', name_en: 'GBP', name_ua: 'Британські фунти' },
  { code: 'PLN', symbol: 'zł', name_en: 'PLN', name_ua: 'Польські злоті' }
];

function numberToWordsEN(num) {
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
                'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
                'seventeen', 'eighteen', 'nineteen'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
  
  if (num === 0) return 'zero';
  if (num < 20) return ones[num];
  if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? ' ' + ones[num % 10] : '');
  if (num < 1000) return ones[Math.floor(num / 100)] + ' hundred' + (num % 100 ? ' and ' + numberToWordsEN(num % 100) : '');
  if (num < 1000000) return numberToWordsEN(Math.floor(num / 1000)) + ' thousand' + (num % 1000 ? ' ' + numberToWordsEN(num % 1000) : '');
  return num.toString();
}

function capitalizeFirst(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export default function PocketbookInvoicePage() {
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [providers, setProviders] = useState([]);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [translating, setTranslating] = useState({});
  
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  
  // Form data - Provider is selectable, Client/Payer is fixed (Pocketbook)
  const [formData, setFormData] = useState({
    // Invoice info
    invoice_number: `${currentYear}${String(currentMonth + 1).padStart(2, '0')}1`,
    invoice_date: currentDate.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '.'),
    month_index: currentMonth,
    year: currentYear,
    
    // Provider info (selectable from list - the person providing services)
    provider_name_ua: '',
    provider_name_en: '',
    provider_tax_id: '',
    provider_address_ua: '',
    provider_address_en: '',
    
    // Client/Payer info (fixed - Pocketbook International SA)
    customer_name: 'Pocketbook International SA',
    customer_address_ua: 'Крочікйо Кортонья, 6, 6900 Лугано, Швейцарія',
    customer_address_en: 'Crocicchio Cortogna, 6, 6900 Lugano, Switzerland',
    
    // Contract info
    contract_name_ua: '',
    contract_name_en: '',
    contract_number: '',
    contract_date: '',
    
    // Service description
    service_description_ua: 'Плата за послуги дизайну',
    service_description_en: 'Payment for services of design development',
    service_summary_en: 'UX/UI development provided under the Agreement, namely:',
    service_summary_ua: 'Послуги з розробки UX/UI, надані відповідно до Договору, а саме:',
    
    // Currency
    currency_ua: 'Долари США',
    currency_en: 'USD',
    
    // Total amount (single field for all tasks)
    total_amount: '',
    
    // Bank info (provider's bank details)
    bank_account: '',
    bank_name: '',
    bank_address: '',
    bank_swift: '',
    bank_iban: '',
    corresp_bank_name: '',
    corresp_bank_address: '',
    corresp_bank_swift: '',
    
    // Tasks (hours per task, no amount per task)
    tasks: [
      { description_en: '', description_ua: '', hours: '' }
    ]
  });

  useEffect(() => {
    fetchProviders();
    
    // Check for imported Redmine tasks
    const redmineTasks = sessionStorage.getItem('redmine_tasks');
    const redmineMonth = sessionStorage.getItem('redmine_month');
    const redmineYear = sessionStorage.getItem('redmine_year');
    
    if (redmineTasks) {
      try {
        const tasks = JSON.parse(redmineTasks);
        setFormData(prev => ({
          ...prev,
          tasks: tasks,
          month_index: redmineMonth ? parseInt(redmineMonth) : prev.month_index,
          year: redmineYear ? parseInt(redmineYear) : prev.year
        }));
        // Clear the session storage after importing
        sessionStorage.removeItem('redmine_tasks');
        sessionStorage.removeItem('redmine_month');
        sessionStorage.removeItem('redmine_year');
        toast.success(`Imported ${tasks.length} tasks from Redmine`);
      } catch (e) {
        console.error('Failed to parse Redmine tasks:', e);
      }
    }
  }, []);

  const fetchProviders = async () => {
    try {
      const response = await api.get('/clients');
      setProviders(response.data);
    } catch (error) {
      console.error('Failed to fetch providers');
    } finally {
      setLoadingProviders(false);
    }
  };

  const handleProviderSelect = (providerId) => {
    setSelectedProviderId(providerId);
    
    if (providerId === 'new' || !providerId) {
      // Clear provider fields for manual entry
      setFormData(prev => ({
        ...prev,
        provider_name_ua: '',
        provider_name_en: '',
        provider_tax_id: '',
        provider_address_ua: '',
        provider_address_en: '',
        contract_name_ua: '',
        contract_name_en: '',
        contract_number: '',
        contract_date: '',
        bank_account: '',
        bank_name: '',
        bank_address: '',
        bank_swift: '',
        bank_iban: '',
        corresp_bank_name: '',
        corresp_bank_address: '',
        corresp_bank_swift: ''
      }));
      return;
    }
    
    const provider = providers.find(p => p.id === providerId);
    if (provider) {
      // Auto-fill all provider fields
      setFormData(prev => ({
        ...prev,
        provider_name_ua: provider.name_ua || provider.name || '',
        provider_name_en: provider.name || '',
        provider_tax_id: provider.tax_id || '',
        provider_address_ua: provider.address_ua || provider.address || '',
        provider_address_en: provider.address_en || provider.address || '',
        contract_name_ua: provider.contract_name_ua || prev.contract_name_ua,
        contract_name_en: provider.contract_name_en || prev.contract_name_en,
        contract_number: provider.contract_number || prev.contract_number,
        contract_date: provider.contract_date || prev.contract_date,
        service_description_ua: provider.service_description_ua || prev.service_description_ua,
        service_description_en: provider.service_description_en || prev.service_description_en,
        currency_en: provider.currency || prev.currency_en,
        // Bank details
        bank_account: provider.bank_account || prev.bank_account,
        bank_name: provider.bank_name || prev.bank_name,
        bank_address: provider.bank_address || prev.bank_address,
        bank_swift: provider.bank_swift || prev.bank_swift,
        bank_iban: provider.bank_iban || prev.bank_iban,
        corresp_bank_name: provider.corresp_bank_name || prev.corresp_bank_name,
        corresp_bank_address: provider.corresp_bank_address || prev.corresp_bank_address,
        corresp_bank_swift: provider.corresp_bank_swift || prev.corresp_bank_swift
      }));
      
      toast.success(`Provider "${provider.name}" loaded`);
    }
  };

  const handleTaskChange = (index, field, value) => {
    const newTasks = [...formData.tasks];
    newTasks[index] = { ...newTasks[index], [field]: value };
    setFormData({ ...formData, tasks: newTasks });
  };

  const translateTask = async (index) => {
    const task = formData.tasks[index];
    if (!task.description_en || task.description_en.trim() === '') {
      toast.error('Please enter English description first');
      return;
    }
    
    setTranslating(prev => ({ ...prev, [index]: true }));
    
    try {
      const response = await api.post('/translate', {
        text: task.description_en,
        source_lang: 'en',
        target_lang: 'uk'
      });
      
      if (response.data.translated) {
        handleTaskChange(index, 'description_ua', response.data.translated);
        toast.success('Translation completed');
      }
    } catch (error) {
      console.error('Translation error:', error);
      toast.error('Translation failed. Please enter manually.');
    } finally {
      setTranslating(prev => ({ ...prev, [index]: false }));
    }
  };

  const addTask = () => {
    if (formData.tasks.length >= 10) {
      toast.error('Maximum 10 tasks allowed');
      return;
    }
    setFormData({
      ...formData,
      tasks: [...formData.tasks, { description_en: '', description_ua: '', hours: '' }]
    });
  };

  const removeTask = (index) => {
    if (formData.tasks.length === 1) return;
    const newTasks = formData.tasks.filter((_, i) => i !== index);
    setFormData({ ...formData, tasks: newTasks });
  };

  const calculateTotalHours = () => {
    return formData.tasks.reduce((sum, task) => sum + (parseFloat(task.hours) || 0), 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const handleGenerate = async (format = 'docx') => {
    const total = parseFloat(formData.total_amount) || 0;
    if (total === 0) {
      toast.error('Please enter the total amount');
      return;
    }

    if (!formData.provider_name_en) {
      toast.error('Please select or enter a provider');
      return;
    }

    setGenerating(true);
    try {
      const payload = {
        invoice_number: formData.invoice_number,
        invoice_date: formData.invoice_date,
        month: MONTHS_EN[formData.month_index],
        year: String(formData.year),
        custom_fields: {
          'MONTH_UA': MONTHS_UA[formData.month_index].toUpperCase(),
          'MONTH_EN': MONTHS_EN[formData.month_index].toUpperCase(),
          'PROVIDER_NAME_UA': formData.provider_name_ua,
          'PROVIDER_NAME_EN': formData.provider_name_en,
          'PROVIDER_TAX_ID': formData.provider_tax_id,
          'PROVIDER_ADDRESS_UA': formData.provider_address_ua,
          'PROVIDER_ADDRESS_EN': formData.provider_address_en,
          'CUSTOMER_NAME': formData.customer_name,
          'CUSTOMER_ADDRESS_UA': formData.customer_address_ua,
          'CUSTOMER_ADDRESS_EN': formData.customer_address_en,
          'CONTRACT_NAME_UA': formData.contract_name_ua,
          'CONTRACT_NAME_EN': formData.contract_name_en,
          'CONTRACT_NUMBER': formData.contract_number,
          'CONTRACT_DATE': formData.contract_date,
          'SERVICE_DESCRIPTION_UA': formData.service_description_ua,
          'SERVICE_DESCRIPTION_EN': formData.service_description_en,
          'SERVICE_SUMMARY': formData.service_summary_en,
          'SERVICE_SUMMARY_UA': formData.service_summary_ua,
          'CURRENCY_UA': formData.currency_ua,
          'CURRENCY_EN': formData.currency_en,
          'BANK_ACCOUNT': formData.bank_account,
          'BANK_NAME': formData.bank_name,
          'BANK_ADDRESS': formData.bank_address,
          'BANK_SWIFT': formData.bank_swift,
          'BANK_IBAN': formData.bank_iban,
          'CORRESP_BANK_NAME': formData.corresp_bank_name,
          'CORRESP_BANK_ADDRESS': formData.corresp_bank_address || '',
          'CORRESP_BANK_SWIFT': formData.corresp_bank_swift,
          'TOTAL_AMOUNT': formatCurrency(total),
          'TOTAL_WORDS_EN': capitalizeFirst(numberToWordsEN(Math.round(total))),
          'TOTAL_WORDS_UA': `${Math.round(total)}`,
          ...formData.tasks.reduce((acc, task, i) => {
            const num = i + 1;
            acc[`TASK_${num}_EN`] = task.description_en || '';
            acc[`TASK_${num}_UA`] = task.description_ua || '';
            acc[`TASK_${num}_HOURS`] = task.hours || '';
            return acc;
          }, {})
        }
      };

      const response = await api.post(
        `/invoices/generate-pocketbook?output_format=${format}`,
        payload,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const monthName = MONTHS_EN[formData.month_index];
      link.setAttribute('download', `Invoice_${formData.provider_name_en.replace(/\s+/g, '_')}_${monthName}_${formData.year}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Invoice downloaded as ${format.toUpperCase()}!`);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="pocketbook-invoice-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/templates')}
            className="h-9 w-9 p-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="page-title">Pocketbook Invoice Generator</h1>
            <p className="page-subtitle">
              Select a provider and generate invoice for Pocketbook International SA
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Provider Selection */}
          <Card className="dashboard-card border-2 border-[#4F46E5]/20 bg-[#4F46E5]/5">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-[#4F46E5]" />
                Select Provider (Service Provider)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SearchableSelect
                options={[{ id: 'new', name: '+ Enter manually' }, ...providers.map(p => ({
                  ...p,
                  name: p.name,
                  contract_number: p.contract_number || ''
                }))]}
                value={selectedProviderId}
                onValueChange={handleProviderSelect}
                placeholder="Search and select a provider..."
                searchPlaceholder="Search by name or contract number..."
                emptyMessage="No providers found"
                disabled={loadingProviders}
                displayKey="name"
                valueKey="id"
                secondaryKey="contract_number"
              />
              {providers.length === 0 && !loadingProviders && (
                <p className="text-sm text-slate-500 mt-2">
                  No providers yet. <a href="/clients" className="text-[#4F46E5] underline">Add providers</a> to quickly generate invoices.
                </p>
              )}
              {selectedProviderId && selectedProviderId !== 'new' && (
                <div className="mt-3 p-3 bg-white rounded-lg border border-slate-200">
                  <p className="text-sm text-emerald-600 font-medium">✓ Provider details loaded</p>
                  <p className="text-xs text-slate-500 mt-1">Provider info, contract, and bank details auto-filled below</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Client/Payer Info (Fixed - Pocketbook) */}
          <Card className="dashboard-card bg-slate-50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building className="w-5 h-5 text-slate-600" />
                Client / Payer (Fixed)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <p className="font-semibold text-slate-900">{formData.customer_name}</p>
                <p className="text-sm text-slate-600 mt-1">{formData.customer_address_en}</p>
                <p className="text-sm text-slate-500 mt-1">{formData.customer_address_ua}</p>
              </div>
            </CardContent>
          </Card>

          {/* Invoice Details */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#4F46E5]" />
                Invoice Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="form-group">
                  <Label>Invoice Number</Label>
                  <Input
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    className="font-mono"
                  />
                </div>
                <div className="form-group">
                  <Label>Invoice Date</Label>
                  <Input
                    value={formData.invoice_date}
                    onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                    placeholder="DD.MM.YYYY"
                  />
                </div>
                <div className="form-group">
                  <Label>Month</Label>
                  <Select 
                    value={String(formData.month_index)} 
                    onValueChange={(v) => setFormData({ ...formData, month_index: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS_EN.map((month, i) => (
                        <SelectItem key={i} value={String(i)}>{month}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="form-group">
                  <Label>Year</Label>
                  <Input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Provider Info */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building className="w-5 h-5 text-[#4F46E5]" />
                Provider Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="form-grid">
                <div className="form-group">
                  <Label>Provider Name (Ukrainian)</Label>
                  <Input
                    value={formData.provider_name_ua}
                    onChange={(e) => setFormData({ ...formData, provider_name_ua: e.target.value })}
                    placeholder="ПІБ українською"
                  />
                </div>
                <div className="form-group">
                  <Label>Provider Name (English)</Label>
                  <Input
                    value={formData.provider_name_en}
                    onChange={(e) => setFormData({ ...formData, provider_name_en: e.target.value })}
                    placeholder="Full name in English"
                  />
                </div>
              </div>
              <div className="form-group">
                <Label>Tax ID (ЄДРПОУ/ІПН)</Label>
                <Input
                  value={formData.provider_tax_id}
                  onChange={(e) => setFormData({ ...formData, provider_tax_id: e.target.value })}
                  placeholder="Tax identification number"
                  className="font-mono"
                />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <Label>Address (Ukrainian)</Label>
                  <Input
                    value={formData.provider_address_ua}
                    onChange={(e) => setFormData({ ...formData, provider_address_ua: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <Label>Address (English)</Label>
                  <Input
                    value={formData.provider_address_en}
                    onChange={(e) => setFormData({ ...formData, provider_address_en: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <Label>Contract Name (UA)</Label>
                  <Input
                    value={formData.contract_name_ua}
                    onChange={(e) => setFormData({ ...formData, contract_name_ua: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <Label>Contract Name (EN)</Label>
                  <Input
                    value={formData.contract_name_en}
                    onChange={(e) => setFormData({ ...formData, contract_name_en: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <Label>Contract Number</Label>
                  <Input
                    value={formData.contract_number}
                    onChange={(e) => setFormData({ ...formData, contract_number: e.target.value })}
                    placeholder="01 04/19"
                  />
                </div>
                <div className="form-group">
                  <Label>Contract Date</Label>
                  <Input
                    value={formData.contract_date}
                    onChange={(e) => setFormData({ ...formData, contract_date: e.target.value })}
                    placeholder="01.04.2019"
                  />
                </div>
              </div>
              
              {/* Service Description - used in invoice services table */}
              <div className="pt-4 border-t border-slate-200">
                <p className="text-sm font-medium text-slate-600 mb-3">Service Description (appears in invoice table)</p>
                <div className="form-grid">
                  <div className="form-group">
                    <Label>Service Description (English)</Label>
                    <Input
                      value={formData.service_description_en}
                      onChange={(e) => setFormData({ ...formData, service_description_en: e.target.value })}
                      placeholder="UX/UI development provided under the Agreement, namely:"
                    />
                  </div>
                  <div className="form-group">
                    <Label>Service Description (Ukrainian)</Label>
                    <Input
                      value={formData.service_description_ua}
                      onChange={(e) => setFormData({ ...formData, service_description_ua: e.target.value })}
                      placeholder="Послуги з розробки UX/UI, надані відповідно до Договору, а саме:"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tasks */}
          <Card className="dashboard-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Tasks / Services</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addTask}>
                <Plus className="w-4 h-4 mr-1" />
                Add Task
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {formData.tasks.map((task, index) => (
                  <div key={index} className="p-4 border border-slate-200 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-700">Task {index + 1}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTask(index)}
                        className="h-8 w-8 p-0 hover:bg-rose-50"
                        disabled={formData.tasks.length === 1}
                      >
                        <Trash2 className="w-4 h-4 text-rose-500" />
                      </Button>
                    </div>
                    <div className="form-group">
                      <Label>Description (English)</Label>
                      <div className="flex gap-2">
                        <Textarea
                          value={task.description_en}
                          onChange={(e) => handleTaskChange(index, 'description_en', e.target.value)}
                          placeholder="Design for Notes application..."
                          rows={2}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => translateTask(index)}
                          disabled={translating[index] || !task.description_en}
                          className="h-auto px-3"
                          title="Translate to Ukrainian"
                        >
                          {translating[index] ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Languages className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                    <div className="form-group">
                      <Label>Description (Ukrainian)</Label>
                      <Textarea
                        value={task.description_ua}
                        onChange={(e) => handleTaskChange(index, 'description_ua', e.target.value)}
                        placeholder="Дизайн для додатка Notes..."
                        rows={2}
                      />
                    </div>
                    <div className="form-group">
                      <Label>Hours</Label>
                      <Input
                        type="number"
                        value={task.hours}
                        onChange={(e) => handleTaskChange(index, 'hours', e.target.value)}
                        className="w-32"
                      />
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Total Amount Section */}
              <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label className="text-base font-semibold">Total Amount</Label>
                    <p className="text-sm text-slate-500">Enter the total amount for all tasks</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select 
                      value={formData.currency_en} 
                      onValueChange={(v) => {
                        const currency = CURRENCIES.find(c => c.code === v);
                        setFormData({ 
                          ...formData, 
                          currency_en: currency.code,
                          currency_ua: currency.name_ua 
                        });
                      }}
                    >
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map((curr) => (
                          <SelectItem key={curr.code} value={curr.code}>
                            {curr.symbol} {curr.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      value={formData.total_amount}
                      onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                      placeholder="0"
                      className="w-40 text-lg font-semibold"
                    />
                  </div>
                </div>
                <div className="mt-2 text-sm text-slate-500">
                  Total hours: {calculateTotalHours()}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bank Info */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#4F46E5]" />
                Provider's Bank Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="form-grid">
                <div className="form-group">
                  <Label>IBAN</Label>
                  <Input
                    value={formData.bank_iban}
                    onChange={(e) => setFormData({ ...formData, bank_iban: e.target.value })}
                    className="font-mono text-sm"
                  />
                </div>
                <div className="form-group">
                  <Label>SWIFT</Label>
                  <Input
                    value={formData.bank_swift}
                    onChange={(e) => setFormData({ ...formData, bank_swift: e.target.value })}
                    className="font-mono"
                  />
                </div>
              </div>
              <div className="form-group">
                <Label>Bank Name</Label>
                <Input
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                />
              </div>
              
              {/* Correspondent Bank Section */}
              <div className="pt-4 border-t border-slate-200">
                <p className="text-sm font-medium text-slate-600 mb-3">Correspondent Bank (optional)</p>
                <div className="space-y-4">
                  <div className="form-grid">
                    <div className="form-group">
                      <Label>Corresp. Bank Name</Label>
                      <Input
                        value={formData.corresp_bank_name}
                        onChange={(e) => setFormData({ ...formData, corresp_bank_name: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <Label>Corresp. SWIFT</Label>
                      <Input
                        value={formData.corresp_bank_swift}
                        onChange={(e) => setFormData({ ...formData, corresp_bank_swift: e.target.value })}
                        className="font-mono"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <Label>Corresp. Bank Address</Label>
                    <Input
                      value={formData.corresp_bank_address}
                      onChange={(e) => setFormData({ ...formData, corresp_bank_address: e.target.value })}
                      placeholder="City, Country"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Summary & Download */}
        <div className="space-y-6">
          <Card className="dashboard-card sticky top-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Download className="w-5 h-5 text-[#4F46E5]" />
                Generate Invoice
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Invoice #</span>
                  <span className="font-mono font-medium">{formData.invoice_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Provider</span>
                  <span className="font-medium truncate max-w-[150px]">{formData.provider_name_en || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Client</span>
                  <span className="font-medium truncate max-w-[150px]">Pocketbook Int.</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Period</span>
                  <span className="font-medium">{MONTHS_EN[formData.month_index]} {formData.year}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Tasks</span>
                  <span className="font-medium">{formData.tasks.filter(t => t.description_en).length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Total Hours</span>
                  <span className="font-medium">{calculateTotalHours()}</span>
                </div>
                <div className="flex justify-between pt-2 border-t">
                  <span className="font-semibold">Total</span>
                  <span className="text-xl font-bold text-[#4F46E5]">
                    {CURRENCIES.find(c => c.code === formData.currency_en)?.symbol}{formatCurrency(formData.total_amount)} {formData.currency_en}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={() => handleGenerate('docx')}
                  disabled={generating}
                  className="w-full bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                  data-testid="generate-docx-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Download DOCX
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={() => handleGenerate('pdf')}
                  disabled={generating}
                  variant="outline"
                  className="w-full"
                  data-testid="generate-pdf-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-sm">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => navigate('/clients')}
              >
                <Users className="w-4 h-4 mr-2" />
                Manage Providers
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
